/**
 * ReconnectingWebSocket
 * 
 * A robust WebSocket wrapper that automatically reconnects when connections are lost
 * and implements exponential backoff for connection retries.
 */
class ReconnectingWebSocket {
  /**
   * Constructor
   * @param {string} url - The WebSocket URL to connect to
   * @param {Object} options - Configuration options
   * @param {number} options.maxReconnectAttempts - Maximum number of reconnection attempts (default: Infinity)
   * @param {number} options.reconnectInterval - Initial reconnection interval in ms (default: 1000)
   * @param {number} options.maxReconnectInterval - Maximum reconnection interval in ms (default: 30000)
   * @param {number} options.reconnectDecay - Rate of increase of the reconnect delay (default: 1.5)
   * @param {boolean} options.debug - Whether to output debug messages to console (default: false)
   */
  constructor(url, options = {}) {
    this.url = url;
    this.options = {
      maxReconnectAttempts: Infinity,
      reconnectInterval: 1000,
      maxReconnectInterval: 30000,
      reconnectDecay: 1.5,
      debug: false,
      ...options
    };

    // Internal state
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.forceClosed = false;

    // Event handlers
    this.onopen = null;
    this.onclose = null;
    this.onmessage = null;
    this.onerror = null;
    
    // Bind methods to ensure 'this' context is preserved
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.send = this.send.bind(this);
    this.reconnect = this.reconnect.bind(this);
    
    // Automatically connect when created
    this.open();
    
    // Log creation
    this.debug('ReconnectingWebSocket instance created');
  }
  
  /**
   * Debug logger
   * @param {string} message - Debug message
   */
  debug(message) {
    if (this.options.debug) {
      console.log(`[ReconnectingWebSocket] ${message}`);
    }
  }
  
  /**
   * Open the WebSocket connection
   */
  open() {
    // Clear any existing reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Don't try to open if already open
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      this.debug('Connection already open or connecting');
      return;
    }
    
    this.forceClosed = false;
    this.debug(`Connecting to ${this.url}`);
    
    try {
      this.socket = new WebSocket(this.url);
      
      this.socket.onopen = (event) => {
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.debug('Connection established');
        
        // Call user's onopen handler if provided
        if (typeof this.onopen === 'function') {
          this.onopen(event);
        }
      };
      
      this.socket.onclose = (event) => {
        this.isConnected = false;
        this.debug(`Connection closed, code: ${event.code}, reason: ${event.reason}`);
        
        // Call user's onclose handler if provided
        if (typeof this.onclose === 'function') {
          this.onclose(event);
        }
        
        // Don't reconnect if explicitly closed or max attempts reached
        if (this.forceClosed) {
          this.debug('Connection closed deliberately, not reconnecting');
          return;
        }
        
        if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
          this.debug('Max reconnect attempts reached, giving up');
          return;
        }
        
        // Schedule reconnection
        this.reconnect();
      };
      
      this.socket.onmessage = (event) => {
        // Call user's onmessage handler if provided
        if (typeof this.onmessage === 'function') {
          this.onmessage(event);
        }
      };
      
      this.socket.onerror = (event) => {
        this.debug('WebSocket error');
        
        // Call user's onerror handler if provided
        if (typeof this.onerror === 'function') {
          this.onerror(event);
        }
      };
    } catch (error) {
      this.debug(`Error during connection: ${error.message}`);
      // Schedule reconnection
      this.reconnect();
    }
  }
  
  /**
   * Close the WebSocket connection
   * @param {number} code - Status code (default: 1000 normal closure)
   * @param {string} reason - Reason for closure
   */
  close(code = 1000, reason = 'Normal closure') {
    this.forceClosed = true;
    
    // Clear any reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.socket) {
      // Only close if not already closed
      if (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) {
        this.debug(`Closing connection, code: ${code}, reason: ${reason}`);
        this.socket.close(code, reason);
      }
    }
  }
  
  /**
   * Send data through the WebSocket
   * @param {string|ArrayBuffer|Blob} data - Data to send
   * @returns {boolean} Whether the send was successful
   */
  send(data) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      this.debug('Unable to send message, socket not open');
      return false;
    }
    
    try {
      this.socket.send(data);
      return true;
    } catch (error) {
      this.debug(`Error sending message: ${error.message}`);
      return false;
    }
  }
  
  /**
   * Calculate the next reconnection delay using exponential backoff
   * @returns {number} The delay in milliseconds
   */
  getReconnectDelay() {
    const delay = Math.min(
      this.options.maxReconnectInterval,
      this.options.reconnectInterval * Math.pow(this.options.reconnectDecay, this.reconnectAttempts)
    );
    
    return delay;
  }
  
  /**
   * Schedule a reconnection attempt
   */
  reconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    this.reconnectAttempts++;
    const delay = this.getReconnectDelay();
    
    this.debug(`Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    this.reconnectTimer = setTimeout(() => {
      this.debug(`Attempting reconnection ${this.reconnectAttempts}`);
      this.open();
    }, delay);
  }
  
  /**
   * Get the current state of the WebSocket
   * @returns {number} The WebSocket ready state or -1 if not initialized
   */
  getState() {
    return this.socket ? this.socket.readyState : -1;
  }
}

// Export for use in other modules if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ReconnectingWebSocket };
}